import { useState } from "react";
import { usePropertyInventory } from "../../../../../hooks/usePropertyInventory";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  alpha,
  Chip,
  Divider,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  ArrowBack,
  CheckCircle,
  Home,
  Person,
  Phone,
  Email,
  CalendarToday,
  Apartment,
  LocalParking,
  Layers,
  Build
} from "@mui/icons-material";
import { Grid } from "@mui/system";
import { useNavigate } from "react-router/dist";

const statusConfig = {
  1: { label: 'Disponible', color: '#4CAF50', icon: CheckCircle },
  2: { label: 'Ocupado', color: '#FF9800', icon: Person },
  3: { label: 'Mantenimiento', color: '#F44336', icon: Build }
};

const apartmentTypeLabels = {
  1: 'Estudio',
  2: '1 Habitación',
  3: '2 Habitaciones',
  4: '3 Habitaciones',
  5: 'Penthouse'
};

const currencyLabels = {
  1: 'CRC',
  2: 'USD'
};

const documentTypeLabels = {
  1: 'Cédula Nacional',
  2: 'DIMEX',
  3: 'Pasaporte'
};
interface CreateApartmentsSummaryProps {
complexId: string;
}

export const CreateApartmentsSummary: React.FC<CreateApartmentsSummaryProps> = ({ complexId }) => {
  const {
    currentStep,
    setCurrentStep,
    apartmentForComplex,
    complex,
    complexFloors,
    complexParkingSpaces,
    createApartmentForComplex,
    errorMessage,
  } = usePropertyInventory();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });
  const [showSuccessView, setShowSuccessView] = useState(false);
  const navigate = useNavigate();
  const handlePrevious = () => {
    const isOccupied = apartmentForComplex?.apartmentStatus === 2;
    setCurrentStep(isOccupied ? currentStep - 1 : currentStep - 2);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    apartmentForComplex.apartmentComplexId = complexId;
    apartmentForComplex.apartmentHistory.isCurrentTenant = true;
    try {
      await createApartmentForComplex(apartmentForComplex);
      if (!errorMessage) {
        setSnackbar({ open: true, message: 'Apartamento creado exitosamente.', severity: 'success' });
        setShowSuccessView(true);
      } else {
        setSnackbar({ open: true, message: errorMessage || 'Error desconocido', severity: 'error' });
      }

    } catch (error) {
      console.error('Error al crear el complejo:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getFloorLabel = (floorId: string) => {
    const floor = complexFloors?.find(f => f.id === floorId);
    return floor ? `Piso ${floor.floorNumber}` : 'N/A';
  };

  const getParkingLabel = (parkingId?: string) => {
    if (!parkingId) return 'Sin asignar';
    const parking = complexParkingSpaces?.find(p => p.id === parkingId);
    return parking ? `Parqueo ${parking.spaceNumber}` : 'N/A';
  };

   const status = statusConfig[apartmentForComplex?.apartmentStatus as keyof typeof statusConfig];
              const StatusIcon = status.icon;
              const isOccupied = apartmentForComplex?.apartmentStatus === 2;
              const { tenant } = apartmentForComplex || {};

  if (showSuccessView) {
    return (
      <Card sx={{ mb: 4, textAlign: 'center', p: 6 }}>
        <CardContent>
          <CheckCircle sx={{ fontSize: 80, color: '#4DB6AC', mb: 2 }} />
          <Typography variant="h4" fontWeight="bold" color="#2E7D7A" sx={{ mb: 2 }}>
            Apartamentos creados exitosamente!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            El registro de apartamentos se ha completado correctamente.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate(`/properties-inventory/complex/${complexId}`)}
            color="primary"
            sx={{ px: 4, py: 1.5, backgroundColor: '#4DB6AC', '&:hover': { backgroundColor: '#26A69A' } }}
          >
            Ir al inicio
          </Button>
        </CardContent>
      </Card>
    );
  } else {
    return (
      <Card sx={{ mb: 4 }}>
        <CardContent sx={{ p: 4 }}>
          {/* Header */}
          <Box sx={{
            textAlign: 'center',
            mb: 4,
            p: 3,
            backgroundColor: alpha('#4DB6AC', 0.1),
            borderRadius: 2
          }}>
            <CheckCircle sx={{ fontSize: 60, color: '#4DB6AC', mb: 2 }} />
            <Typography variant="h4" fontWeight="bold" color="#2E7D7A" sx={{ mb: 1 }}>
              Resumen Final
            </Typography>
            <Typography variant="h6" color="text.secondary">
              {complex?.name}
            </Typography>
          </Box>

          {/* Lista de Apartamentos */}
          <Typography variant="h5" fontWeight="bold" color="#2E7D7A" sx={{ mb: 3 }}>
            Detalle de Apartamento {apartmentForComplex?.apartmentNumber}
          </Typography>

          <Grid container spacing={3}>
             
                <Grid size={{ xs: 12 }}>
                  <Card sx={{
                    backgroundColor: '#FAFAFA',
                    border: '2px solid',
                    borderColor: status.color,
                    position: 'relative',
                    overflow: 'visible'
                  }}>
                    {/* Badge de Estado */}
                    <Box sx={{
                      position: 'absolute',
                      top: -12,
                      right: 20,
                      backgroundColor: status.color,
                      color: 'white',
                      px: 2,
                      py: 0.5,
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      boxShadow: 2
                    }}>
                      <StatusIcon sx={{ fontSize: 18 }} />
                      <Typography variant="body2" fontWeight="bold">
                        {status.label}
                      </Typography>
                    </Box>

                    <CardContent sx={{ p: 3, pt: 4 }}>
                      {/* Header del Apartamento */}
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <Home sx={{ fontSize: 32, color: '#4DB6AC', mr: 2 }} />
                        <Box>
                          <Typography variant="h6" fontWeight="bold" color="#2E7D7A">
                            Apartamento {apartmentForComplex?.apartmentNumber}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                            {apartmentTypeLabels[apartmentForComplex?.apartmentType as keyof typeof apartmentTypeLabels] || 'N/A'}
                          </Typography>
                        </Box>
                        <Box sx={{ ml: 'auto' }}>
                          <Typography variant="h6" fontWeight="bold" color="#2E7D7A">
                            {currencyLabels[apartmentForComplex?.currency as keyof typeof currencyLabels]} {apartmentForComplex?.baseRentAmount.toLocaleString()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            /mes
                          </Typography>
                        </Box>
                      </Box>

                      <Grid container spacing={3}>
                        {/* Columna Izquierda - Info del Apartamento */}
                        <Grid size={{ xs: 12, md: isOccupied ? 6 : 12 }}>
                          <Typography variant="subtitle2" fontWeight="bold" color="#2E7D7A" sx={{ mb: 2 }}>
                            Información del Apartamento
                          </Typography>

                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Layers sx={{ fontSize: 20, color: '#757575' }} />
                              <Typography variant="body2">
                                <strong>Piso:</strong> {getFloorLabel(apartmentForComplex?.assignedFloorId)}
                              </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LocalParking sx={{ fontSize: 20, color: '#757575' }} />
                              <Typography variant="body2">
                                <strong>Parqueo:</strong> {getParkingLabel(apartmentForComplex?.assignedParkingSpaceId)}
                              </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Apartment sx={{ fontSize: 20, color: '#757575' }} />
                              <Typography variant="body2">
                                <strong>Área:</strong> {apartmentForComplex?.squareMeters} m² | <strong>Baños:</strong> {apartmentForComplex?.bathrooms}
                              </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              {apartmentForComplex?.hasBalcony && (
                                <Chip
                                  label="Balcón"
                                  size="small"
                                  sx={{ backgroundColor: alpha('#4DB6AC', 0.1) }}
                                />
                              )}
                              {apartmentForComplex?.hasOwnParking && (
                                <Chip
                                  label="Parqueo Propio"
                                  size="small"
                                  sx={{ backgroundColor: alpha('#4DB6AC', 0.1) }}
                                />
                              )}
                            </Box>

                            {apartmentForComplex?.description && (
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                  {apartmentForComplex?.description}
                                </Typography>
                              </Box>
                            )}

                            {apartmentForComplex?.apartmentServices && apartmentForComplex?.apartmentServices.length > 0 && (
                              <Box sx={{ mt: 2 }}>
                                <Typography variant="caption" fontWeight="bold" color="#2E7D7A">
                                  Servicios: {apartmentForComplex?.apartmentServices.length}
                                </Typography>
                              </Box>
                            )}

                            <Box sx={{ mt: 1 }}>
                              <Typography variant="body2">
                                <strong>Depósito:</strong> {currencyLabels[apartmentForComplex?.currency as keyof typeof currencyLabels]} {apartmentForComplex?.securityDeposit.toLocaleString()}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>

                        {/* Columna Derecha - Info del Inquilino (solo si está ocupado) */}
                        {isOccupied && tenant && (
                          <>
                            <Grid size={{ xs: 12, md: 6 }}>
                              <Divider orientation="vertical" sx={{ display: { xs: 'none', md: 'block' }, mr: 3 }} />

                              <Typography variant="subtitle2" fontWeight="bold" color="#2E7D7A" sx={{ mb: 2 }}>
                                Información del Inquilino
                              </Typography>

                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Person sx={{ fontSize: 20, color: '#757575' }} />
                                  <Typography variant="body2">
                                    <strong>Nombre:</strong> {tenant.firstName} {tenant.lastName}
                                  </Typography>
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body2">
                                    <strong>{documentTypeLabels[tenant.documentType as keyof typeof documentTypeLabels]}:</strong> {tenant.documentNumber}
                                  </Typography>
                                </Box>

                                {tenant.email && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Email sx={{ fontSize: 20, color: '#757575' }} />
                                    <Typography variant="body2">
                                      {tenant.email}
                                    </Typography>
                                  </Box>
                                )}

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Phone sx={{ fontSize: 20, color: '#757575' }} />
                                  <Typography variant="body2">
                                    {tenant.primaryPhone}
                                  </Typography>
                                </Box>

                                {tenant.profession && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2">
                                      <strong>Profesión:</strong> {tenant.profession}
                                    </Typography>
                                  </Box>
                                )}

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <CalendarToday sx={{ fontSize: 20, color: '#757575' }} />
                                  <Typography variant="body2">
                                    <strong>Fecha de Ingreso:</strong> {new Date(apartmentForComplex?.apartmentHistory.moveInDate).toLocaleDateString('es-ES')}
                                  </Typography>
                                </Box>

                              </Box>
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
          </Grid>

          {/* Botones de navegación */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, pt: 3, borderTop: '2px solid #E0E0E0' }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBack />}
              onClick={handlePrevious}
              disabled={isSubmitting}
              sx={{
                borderColor: '#4DB6AC',
                color: '#4DB6AC',
                '&:hover': {
                  borderColor: '#26A69A',
                  backgroundColor: alpha('#4DB6AC', 0.1)
                }
              }}
            >
              Anterior
            </Button>

            <Button
              variant="contained"
              size="large"
              startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <CheckCircle />}
              onClick={handleSubmit}
              disabled={isSubmitting}
              sx={{
                backgroundColor: '#4DB6AC',
                px: 4,
                py: 1.5,
                '&:hover': {
                  backgroundColor: '#26A69A',
                },
                '&:disabled': {
                  backgroundColor: '#E0E0E0'
                }
              }}
            >
              {isSubmitting ? 'Creando Apartamentos...' : 'Crear Apartamentos'}
            </Button>
          </Box>
        </CardContent>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Card>
    );
  }
};